import { useEffect } from 'react';

import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { format, isAfter, parseISO, startOfMonth, subMonths } from 'date-fns';
import { useSelector } from 'react-redux';

import { VideaTypography } from '../../common-components/elements/VideaTypography/VideaTypography';
import { fontFamiliesNeueHaasGroteskDisplayPro } from '../../theme/videa-style-tokens';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';
import { PracticeFeatureFlagKey, useFeatureFlag } from '../../utils/user';

import { ComingSoon } from './ComingSoon';
import { Flex } from './CommonStyles';
import { HowToCard } from './HowToCard';
import { IncompleteTreatmentCountsCard } from './IncompleteTreatmentCountsCard';
import { PatientFindingsCard } from './PatientFindingsCard';
import { TreatmentValuesCard } from './TreatmentValuesCard';

const DDAIMetricsContainer = styled.div`
  max-width: 78em;
  box-sizing: border-box;
  padding: 1.5em;
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

const CardsContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  min-height: 22.5em;
  gap: 0.625em;

  .MuiTypography-root {
    font-family: ${fontFamiliesNeueHaasGroteskDisplayPro};
  }
`;

export const DDAIMetrics = () => {
  const practiceOnboardingDate: string | undefined | null = useSelector(
    (state: any) => state.login.user.practice,
  )?.onboardingDate;
  const { trackEvent } = useTrackEvent();
  const isDDAIRoiV1Enabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.DDAI_ROI_PAGE_V1_ENABLED,
  )?.value;

  // Onboarding date is invalid and we should not show metrics if either:
  // 1. There is no onboarding date
  // 2. The onboarding date is after the first day of the previous month
  // E.g. If today is Jan 26th, and the onboarding date was anytime after Dec 1st, it would be invalid
  // due to lacking a full month of data. Once it was Feb 1st, it would then be a valid onboarding date.
  const isInvalidOnboardingDate =
    isDDAIRoiV1Enabled &&
    (!practiceOnboardingDate ||
      isAfter(
        parseISO(practiceOnboardingDate),
        startOfMonth(subMonths(new Date(), 1)),
      ));

  useEffect(() => {
    trackEvent(mixpanelEvents.analyticsView, {
      comingSoon: true,
    });
    const imageViewTime = performance.now();

    return () => {
      const ddaiMetricsExitTime = performance.now();
      const secondsElapsed =
        Math.round(ddaiMetricsExitTime - imageViewTime) / 1000;
      trackEvent(mixpanelEvents.analyticsViewDuration, {
        comingSoon: true,
        duration: secondsElapsed,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isDDAIRoiV1Enabled && !isInvalidOnboardingDate) {
    return (
      <DDAIMetricsContainer>
        <Flex className="space-between">
          <VideaTypography variant="headingMedium" component="h3">
            <Trans>Monthly Recap</Trans>
          </VideaTypography>
          <VideaTypography variant="headingMedium" component="h3">
            {format(subMonths(new Date(), 1), 'MMMM yyyy')}
          </VideaTypography>
        </Flex>
        <CardsContainer>
          <PatientFindingsCard />
          <IncompleteTreatmentCountsCard />
          <TreatmentValuesCard />
        </CardsContainer>
        <HowToCard />
      </DDAIMetricsContainer>
    );
  }

  return (
    <DDAIMetricsContainer>
      <CardsContainer>
        <ComingSoon isInvalidOnboardingDate={isInvalidOnboardingDate} />
      </CardsContainer>
      {isInvalidOnboardingDate && <HowToCard />}
    </DDAIMetricsContainer>
  );
};
